import React, { useCallback, useMemo } from 'react'
import { ErrorMessage } from '@hookform/error-message'
import clsx from 'clsx'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { ArrowForwardIosSharp } from '@mui/icons-material'
import {
  Badge,
  Box,
  Collapse,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Icon, Tooltip } from 'src/components/atoms'
import { getHighlightColor } from 'src/components/molecules/OCRHighlightOverlay/constants'
import TextFieldWithComma from 'src/components/molecules/TextField/textFieldWithComma'
import {
  hasExtendedAmountDigits,
  isReadItemToggleHidden,
  VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES,
} from 'src/domain/org'
import color from 'src/styles/color'
import { isNumber } from 'src/utils/number'
import { getAmountLimitValue, getDigitLimitErrorMessage } from '../../constants'

const timeout = 500
const rowHeight = 44

export type TenantSalesReportTableRowProps = {
  orgCode: string
  cells: Cell[]
  childRows: ChildRow[]
  inputCell: InputCell
  sumCell?: number
  tooltipText?: string
  rowIndex: number
  registerIndex: number
  error?: boolean
  hasDiff: boolean
  onClickValue: (value: string, childIndex?: number) => void
  showHighlights?: boolean
  highlightColorIndex?: number
  expanded?: boolean
  onToggleExpand?: () => void
} & React.StyledProps

export type Cell = {
  key: string
  title: string
}

export type ChildRow = {
  key: string
  cells: Cell[]
  inputCell: InputCell
}

export type InputCell = {
  name: string
  defaultValue?: number
  placeholder?: string
}

const useStyles = makeStyles({
  expandIcon: {
    height: 8,
    margin: 'auto 0',
    color: color.black,
    transition: `${timeout}ms`,
  },
  expanded: {
    transform: 'rotate(90deg)',
  },
  row: {
    height: rowHeight,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  parentRow: {
    '&:hover': {
      backgroundColor: color.black50,
    },
  },
  noToggleRow: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  childRow: {
    height: 0,
    transition: `${timeout}ms`,
  },
  expandedChildRow: {
    height: rowHeight,
  },
  cell: {
    width: 184,
    maxWidth: 184,
    boxSizing: 'border-box',
    padding: '0 8px',
    borderBottom: `solid 1px ${color.lightGray}`,
    transition: `${timeout}ms`,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  invalidDigit: {
    verticalAlign: 'text-top',
  },
  parentValueInvalid: {
    marginTop: 8,
  },
  invalidTextField: {
    borderColor: color.red,
  },
  invalidSumNumber: {
    paddingBottom: '0px !important',
  },
  firstCell: {
    paddingLeft: 24,
  },
  hideCell: {
    borderBottom: 'none',
  },
  inputField: {
    width: 108,
    height: 32,
    padding: '0 8px',
    '& input': {
      marginRight: 0,
      fontSize: 12,
    },
  },

  // 三井系org(007/988)の11桁入力時の表示幅を確保するための拡張幅。
  // hasExtendedAmountDigits(orgCode) が true の org にのみ適用し、対象外orgのレイアウトへは影響させない。
  inputFieldExtended: {
    width: 160,
  },
  haveChildCell: {
    paddingLeft: 0,
  },
  typography: {
    fontSize: 15,
    lineHeight: '17px',
    wordBreak: 'break-word',
  },
  parentTitle: {
    fontWeight: '900',
  },
  valueTypography: {
    padding: '8px',
    borderRadius: 8,
    wordBreak: 'keep-all',
    '&:hover': {
      backgroundColor: color.black50,
    },
  },
  disableClickTypography: {
    padding: 8,
    wordBreak: 'keep-all',
  },
  errorText: {
    color: color.red,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  tooltipWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  iconAndTypography: {
    display: 'flex',
    width: 160,
    maxWidth: 160,
  },
  error: {
    margin: '4px 0px',
    color: color.red,
    lineHeight: '16px',
  },
  wrapper: {
    display: 'flex',
  },
  childInvalidBox: {
    borderColor: color.red,
    marginTop: 8,
  },
  diffBadge: {
    '& .MuiBadge-badge': {
      top: 2,
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: color.primary,
    },
  },
})

const TenantSalesReportTableRow: React.StyledFC<
  TenantSalesReportTableRowProps
> = ({
  className,
  orgCode,
  cells,
  inputCell,
  sumCell,
  childRows,
  tooltipText,
  rowIndex,
  registerIndex,
  error,
  hasDiff,
  onClickValue,
  showHighlights,
  highlightColorIndex,
  expanded: expandedProp,
  onToggleExpand,
}: TenantSalesReportTableRowProps) => {
  const classes = useStyles()
  // org に応じた金額の上限値とエラーメッセージ（三井系: 11桁 / その他: 9桁）
  const limitValue = getAmountLimitValue(orgCode)
  const digitLimitErrorMessage = getDigitLimitErrorMessage(orgCode)
  // 三井系org(007/988)は11桁入力に対応するため入力欄幅を拡張する
  const isExtendedAmountDigits = hasExtendedAmountDigits(orgCode)
  const readItemToggleHidden = isReadItemToggleHidden(orgCode)
  // NOTE: read_item トグル非表示orgでは、子行は DOM 上に残し（react-hook-form の登録維持のため）、常に閉じた状態にする。
  const expanded = readItemToggleHidden
    ? false
    : (expandedProp ?? Boolean(error))
  const {
    formState: { errors },
    trigger,
  } = useFormContext()

  const handleClick = () => {
    // NOTE: input や Clear ボタンを触った時に閉じたり開いたりしないように
    if (
      document.activeElement?.tagName === 'INPUT' ||
      document.activeElement?.getAttribute('data-testid') === 'cancel-button'
    )
      return
    if (readItemToggleHidden) return
    onToggleExpand?.()
  }

  // 親行の最初のセルにのみ左ボーダーを適用する
  const highlightBorderStyle = useMemo(() => {
    if (!showHighlights || !expanded || highlightColorIndex === undefined) {
      return undefined
    }
    return {
      borderLeft: `8px solid ${getHighlightColor(highlightColorIndex).marker}`,
    }
  }, [showHighlights, expanded, highlightColorIndex])

  const handlerClickValue = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    value: string,
    index?: number
  ) => {
    e.stopPropagation()
    onClickValue(value, index)
  }

  const cellText = (title: string, isBold: boolean, childIndex?: number) => {
    const isValue = isNumber(title)
    return (
      <Typography
        className={clsx(
          classes.typography,
          isValue && classes.valueTypography,
          isBold && classes.parentTitle,
          error && classes.errorText
        )}
        data-testid={isValue ? 'value' : 'label'}
        onClick={(e) => isValue && handlerClickValue(e, title, childIndex)}
      >
        {title}
      </Typography>
    )
  }

  const align = (value: string, isFirst: boolean) => {
    if (isFirst) return 'left'
    if (isNumber(value) || value === '-') return 'right'
    return 'center'
  }

  const options: RegisterOptions = {
    max: {
      value: limitValue,
      message: digitLimitErrorMessage,
    },
  }

  // NOTE: 桁数上限（org により 9桁/11桁）を超えて入力されたときの判定処理
  const isParentNotSumValueInvalid = Boolean(
    errors.registers &&
      Array.isArray(errors.registers) &&
      errors?.registers?.[registerIndex]?.rows?.[rowIndex]?.value
  )
  const isParentSumValueInvalid =
    typeof sumCell === 'number' && sumCell > limitValue
  const isEitherParentValueInvalid =
    isParentNotSumValueInvalid || isParentSumValueInvalid
  const isChildValueInvalid = useCallback(
    (childIndex: number) =>
      Boolean(
        errors.registers &&
          Array.isArray(errors.registers) &&
          errors.registers[registerIndex]?.rows?.[rowIndex]?.children?.[
            childIndex
          ]?.value
      ),
    [errors.registers, registerIndex, rowIndex]
  )

  return (
    <>
      <TableRow
        className={clsx(
          className,
          classes.row,
          classes.parentRow,
          readItemToggleHidden && classes.noToggleRow
        )}
        onClick={handleClick}
      >
        {cells.map((cell, index) => {
          const isFirst = index === 0
          const noChild = childRows.length === 0

          return (
            <TableCell
              key={cell.key}
              className={clsx(
                classes.cell,
                isFirst && classes.firstCell,
                !noChild &&
                  !readItemToggleHidden &&
                  isFirst &&
                  classes.haveChildCell,
                isEitherParentValueInvalid && classes.invalidDigit
              )}
              align={align(cell.title, isFirst)}
              style={isFirst ? highlightBorderStyle : undefined}
            >
              {isFirst ? (
                <Box className={classes.wrapper}>
                  <Box className={classes.iconAndTypography}>
                    {!noChild && !readItemToggleHidden && (
                      <ArrowForwardIosSharp
                        className={clsx(
                          classes.expandIcon,
                          expanded && classes.expanded
                        )}
                      />
                    )}
                    {cellText(cell.title, true)}
                  </Box>
                  {tooltipText && (
                    <Box className={classes.tooltipWrapper}>
                      <Tooltip title={tooltipText} placement="top">
                        <div className={classes.iconWrapper}>
                          <Icon icon="tips" />
                        </div>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ) : (
                cellText(cell.title, isFirst)
              )}
            </TableCell>
          )
        })}
        <TableCell
          className={clsx(
            classes.cell,
            isEitherParentValueInvalid && classes.invalidDigit
          )}
        >
          <Box
            className={clsx(
              isEitherParentValueInvalid && classes.parentValueInvalid
            )}
          >
            <Badge
              variant="dot"
              invisible={
                !hasDiff ||
                !VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES.includes(orgCode)
              }
              className={classes.diffBadge}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <TextFieldWithComma
                className={clsx(
                  classes.inputField,
                  isExtendedAmountDigits && classes.inputFieldExtended,
                  isParentNotSumValueInvalid && classes.invalidTextField
                )}
                name={inputCell.name}
                defaultValue={inputCell.defaultValue}
                placeholder={inputCell.placeholder}
                options={options}
                onKeyPress={() => trigger(inputCell.name)}
              />
            </Badge>
          </Box>
          <ErrorMessage
            errors={errors}
            name={inputCell.name}
            render={({ message }) => (
              <Typography
                variant="body2"
                component="p"
                className={classes.error}
              >
                {message}
              </Typography>
            )}
          />
        </TableCell>
        {typeof sumCell === 'number' && (
          <TableCell
            className={clsx(
              classes.cell,
              isEitherParentValueInvalid && classes.invalidDigit
            )}
            align={align(sumCell.toLocaleString(), false)}
          >
            <Box
              className={clsx(
                isEitherParentValueInvalid && classes.parentValueInvalid
              )}
            >
              <Typography
                className={clsx(
                  classes.typography,
                  classes.disableClickTypography,
                  isParentSumValueInvalid && classes.invalidSumNumber
                )}
              >
                {sumCell.toLocaleString()}
              </Typography>
            </Box>
            {isParentSumValueInvalid && (
              <Typography
                variant="body2"
                component="p"
                className={classes.error}
              >
                {digitLimitErrorMessage}
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
      {childRows.map((child, childIndex) => (
        <TableRow
          key={child.key}
          className={clsx(
            className,
            classes.row,
            classes.childRow,
            expanded && classes.expandedChildRow,
            isChildValueInvalid(childIndex) && classes.invalidDigit
          )}
        >
          {child.cells.map((cell, index) => {
            const isFirst = index === 0
            return (
              <TableCell
                key={cell.key}
                className={clsx(
                  classes.cell,
                  isFirst && classes.firstCell,
                  !expanded && classes.hideCell
                )}
                align={align(cell.title, isFirst)}
              >
                <Collapse key={cell.key} in={expanded}>
                  {cellText(cell.title, false, childIndex)}
                </Collapse>
              </TableCell>
            )
          })}
          <TableCell
            className={clsx(classes.cell, !expanded && classes.hideCell)}
            align="left"
          >
            <Box
              className={clsx(
                expanded &&
                  isChildValueInvalid(childIndex) &&
                  classes.childInvalidBox
              )}
            >
              <Collapse in={expanded}>
                <TextFieldWithComma
                  className={clsx(
                    classes.inputField,
                    isExtendedAmountDigits && classes.inputFieldExtended,
                    isChildValueInvalid(childIndex) && classes.invalidTextField
                  )}
                  name={child.inputCell.name}
                  defaultValue={child.inputCell.defaultValue}
                  placeholder={child.inputCell.placeholder}
                  options={options}
                  onKeyPress={() => trigger(child.inputCell.name)}
                />
                <ErrorMessage
                  errors={errors}
                  name={child.inputCell.name}
                  render={({ message }) => (
                    <Typography
                      variant="body2"
                      component="p"
                      className={classes.error}
                    >
                      {message}
                    </Typography>
                  )}
                />
              </Collapse>
            </Box>
          </TableCell>
          {typeof sumCell === 'number' && (
            <TableCell
              className={clsx(classes.cell, !expanded && classes.hideCell)}
              align="right"
            >
              <Collapse in={expanded}>
                <Typography
                  className={clsx(
                    classes.typography,
                    classes.disableClickTypography
                  )}
                >
                  -
                </Typography>
              </Collapse>
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  )
}

export default TenantSalesReportTableRow
